import { AccountType } from '@/data/usecases/account/account-type'
import {
  makeCreateUserProfilePage,
  makeDashboardPage,
  makeDependentEnrollmentPage,
  makeDependentHolderSearchPage,
  makeDependentRegistrationPage,
  makeDependentSearchPage,
  makeEditUserProfilePage,
  makeRequestsConfigurationPage,
  makeUpgradeEnrollmentPage
} from '@/main/factories/pages/company-manager-factory'
import CompanyManagerDashboardLoader from '@/main/routes/loaders/company-manager/dashboard/company-manager-dashboard-loader'
import { ValidAccountState } from '@/presentation/contexts/auth/account-provider'
import useAccount from '@/presentation/hooks/auth/use-account'
import { LDClient, useLDClient } from 'launchdarkly-react-client-sdk'
import { useQueryClient } from 'react-query'
import { RouteObject } from 'react-router-dom'
import CompanyManagerDependentLicensesLoader from '../loaders/company-manager/dependent/company-manager-dependent-licenses-loader'
import CompanyManagerDependentRegistrationLoader from '../loaders/company-manager/dependent/company-manager-dependent-registration-loader'
import CompanyManagerDependentSearchLoader from '../loaders/company-manager/dependent/company-manager-dependent-search-loader'
import CompanyManagerCreateUserProfileLoader from '../loaders/company-manager/user/company-manager-create-user-profile-loader'
import CompanyManagerUserProfileLoader from '../loaders/company-manager/user/company-manager-user-profile-loader'
import PathConstants from '../path-contants'
import CompanyManagerDependentConfigurationLoader from '../loaders/company-manager/dependent/company-manager-dependent-configuration-loader'
import CompanyManagerUpgradeRequestLoader from '../loaders/company-manager/upgrade/upgrade-request-loader'
import LoggedLayout from '@/presentation/components/ui/layout/logged-layout'

const {
  COMPANY_MANAGER_DASHBOARD,
  COMPANY_MANAGER_CREATE_USER_PROFILE,
  COMPANY_MANAGER_EDIT_USER_PROFILE,
  COMPANY_MANAGER_EDIT_DEPENDENT_USER_PROFILE,
  COMPANY_MANAGER_DEPENDENT_LIST,
  COMPANY_MANAGER_HOLDER_SEARCH,
  COMPANY_MANAGER_DEPENDENT_REGISTRATION,
  COMPANY_MANAGER_REQUESTS_CONFIGURATION,
  COMPANY_MANAGER_DEPENDENT_ENROLLMENT,
  COMPANY_MANAGER_UPGRADE_ENROLLMENT
} = PathConstants.Routes

const BuildCompanyManagerRoutes = (): RouteObject[] => {
  const [account] = useAccount() as ValidAccountState
  const queryClient = useQueryClient()
  const ldClient = useLDClient() as LDClient
  return [
    {
      element: (
        <LoggedLayout navMenu="companyManager" footerVariant="compact" enableSearch={false} />
      ),
      children: [
        {
          path: COMPANY_MANAGER_DASHBOARD.value,
          element: makeDashboardPage(),
          loader: new CompanyManagerDashboardLoader(account, queryClient, ldClient).loader
        },
        {
          path: COMPANY_MANAGER_CREATE_USER_PROFILE.value,
          element: makeCreateUserProfilePage(),
          loader: new CompanyManagerCreateUserProfileLoader(account, queryClient, ldClient).loader
        },
        {
          path: COMPANY_MANAGER_EDIT_USER_PROFILE.value,
          element: makeEditUserProfilePage(),
          loader: new CompanyManagerUserProfileLoader(
            account,
            queryClient,
            ldClient,
            AccountType.Enum.COLLABORATOR
          ).loader
        },
        {
          path: COMPANY_MANAGER_EDIT_DEPENDENT_USER_PROFILE.value,
          element: makeEditUserProfilePage(),
          loader: new CompanyManagerUserProfileLoader(
            account,
            queryClient,
            ldClient,
            AccountType.Enum.DEPENDENT
          ).loader
        },
        {
          path: COMPANY_MANAGER_DEPENDENT_LIST.value,
          element: makeDependentSearchPage(),
          loader: new CompanyManagerDependentSearchLoader(account, queryClient, ldClient).loader
        },
        {
          path: COMPANY_MANAGER_HOLDER_SEARCH.value,
          element: makeDependentHolderSearchPage(account),
          loader: new CompanyManagerDependentLicensesLoader(account, queryClient, ldClient).loader
        },
        {
          path: COMPANY_MANAGER_DEPENDENT_REGISTRATION.value,
          element: makeDependentRegistrationPage(),
          loader: new CompanyManagerDependentRegistrationLoader(account, queryClient, ldClient)
            .loader
        },
        {
          path: COMPANY_MANAGER_REQUESTS_CONFIGURATION.value,
          element: makeRequestsConfigurationPage(),
          loader: new CompanyManagerDependentConfigurationLoader(account, queryClient, ldClient)
            .loader
        },
        {
          path: COMPANY_MANAGER_DEPENDENT_ENROLLMENT.value,
          element: makeDependentEnrollmentPage(),
          loader: new CompanyManagerDependentConfigurationLoader(account, queryClient, ldClient)
            .loader
        },
        {
          path: COMPANY_MANAGER_UPGRADE_ENROLLMENT.value,
          element: makeUpgradeEnrollmentPage(),
          loader: new CompanyManagerUpgradeRequestLoader(account, queryClient, ldClient).loader
        }
      ]
    }
  ]
}

export default BuildCompanyManagerRoutes
